/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Plus, Search, Grid, List, MoreVertical, Copy, Trash2, Eye, Calendar, Settings } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useMaps } from "@/hooks/use-maps"
import type { MapData } from "@/services/SiteService"
import { useNavigate } from "react-router"
import { getGoogleStaticMapUrl } from "@/lib/google-static-map"
import { siteAdminUrlBuilder, siteMapUrlBuilder } from "@/lib/url-builder"
import { useConfig } from "@/config/ConfigProvider"
import GoogleMapPin from "@/components/maps/GoogleMapPin"
import type { GPSCoords } from "@/lib/image-utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export function SiteListPage() {
  const navigate = useNavigate();
  const config = useConfig();
  const { maps, tenants, loading, error, createMap, deleteMap, fetchTenants } = useMaps();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMapForDelete, setSelectedMapForDelete] = useState<MapData | null>(null);
  const [newMapName, setNewMapName] = useState("");
  const [newMapDescription, setNewMapDescription] = useState("");
  const [newSiteTenantId, setNewSiteTenantId] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [siteCoords, setSiteCoords] = useState<GPSCoords | null>(null);
  const [siteZoom, setSiteZoom] = useState(15);
  
  const apiKey = config?.GOOGLE_MAPS_API_KEY;

  const filteredMaps = maps.filter(
    (map) =>
      map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      map.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleCreateMap = async () => {
    if (!newMapName.trim()) return

    setIsCreating(true)
    const result = await createMap({
      name: newMapName.trim(),
      description: newMapDescription.trim() || undefined,
      tenantId: newSiteTenantId,
      latitude: siteCoords?.latitude ?? 0,
      longitude: siteCoords?.longitude ?? 0,
      zoom: siteZoom,
    })

    if (result) {
      setShowCreateDialog(false)
      setNewMapName("")
      setNewMapDescription("")
      // Automatically open the new map
    }
    setIsCreating(false)
  }

  const handleDeleteMap = async () => {
    if (!selectedMapForDelete) return

    const success = await deleteMap(selectedMapForDelete.id)
    if (success) {
      setShowDeleteDialog(false)
      setSelectedMapForDelete(null)
    }
  }

  useEffect(() => {
    const getData = async () => {
      const tenants = await fetchTenants();
      if (tenants.length > 0) {
        setNewSiteTenantId(tenants[0].id);
      }
    }
    getData();
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your maps...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 mb-4">Error loading maps</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Sites</h1>
              <p className="text-gray-600 mt-1">Manage your site maps and features</p>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create New Site
            </Button>
          </div>

          {/* Search and View Controls */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search maps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Maps Grid/List */}
        {filteredMaps.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              {searchTerm ? "No maps found matching your search." : "No maps created yet."}
            </div>
            {!searchTerm && (
              <Button onClick={() => setShowCreateDialog(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Site
              </Button>
            )}
          </div>
        ) : (
          <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
            {filteredMaps.map((site) => (
              <Card
                key={site.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  viewMode === "list" ? "flex items-center" : ""
                }`}
                onClick={() => navigate(siteMapUrlBuilder(site.tenantId,site.id, null))}
              >
                {viewMode === "grid" ? (
                  <>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-gray-900 mb-1">{site.name}</CardTitle>
                          {site.description && <p className="text-sm text-gray-600 line-clamp-2">{site.description}</p>}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e: any) => {
                                e.stopPropagation()
                                navigate(siteMapUrlBuilder(site.tenantId, site.id, null))
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Open Map
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e: any) => {
                                e.stopPropagation()
                                navigate(siteAdminUrlBuilder(site.tenantId, site.id))
                              }}
                            >
                              <Settings className="h-4 w-4 mr-2" />
                              Site Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e: any) => {
                                e.stopPropagation()
                                setSelectedMapForDelete(site)
                                setShowDeleteDialog(true)
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-start gap-4">
                        <img
                          src={apiKey ? getGoogleStaticMapUrl({
                            latitude: site.latitude,
                            longitude: site.longitude,
                            apiKey,
                            zoom: site.zoom,
                            width: 80,
                            height: 80,
                          }) : undefined}
                          alt={`${site.name} map preview`}
                          className="h-20 w-20 rounded-md object-cover"
                          loading="lazy"
                        />
                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3 text-gray-600" />
                              <span className="text-xs text-gray-600">{formatDate(site.modifiedAt)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3 text-gray-600" />
                              <span className="text-xs text-gray-600">{site.tenantName}</span>
                            </div>                        
                        </div>
                      </div>
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center justify-between p-4 w-full">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{site.name}</h3>
                      {site.description && <p className="text-sm text-gray-600 mt-1">{site.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span>{site.tenantName}</span>
                        <span>{formatDate(site.modifiedAt)}</span>

                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e: any) => {
                            e.stopPropagation()
                            alert(site.id)
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Open Map
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e: any) => {
                            e.stopPropagation()
                            navigate(siteAdminUrlBuilder(site.tenantId, site.id))
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Site Admin
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e: any) => {
                            e.stopPropagation()
                            setSelectedMapForDelete(site)
                            setShowDeleteDialog(true)
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Create Map Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Site</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="map-name">Map Name</Label>
                <Input
                  id="map-name"
                  value={newMapName}
                  onChange={(e) => setNewMapName(e.target.value)}
                  placeholder="Enter map name..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newMapName.trim()) {
                      handleCreateMap()
                    }
                  }}
                />
              </div>
              <div>
                <Label htmlFor="map-description">Description (Optional)</Label>
                <Textarea
                  id="map-description"
                  value={newMapDescription}
                  onChange={(e) => setNewMapDescription(e.target.value)}
                  placeholder="Describe your map..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="site-tenant">Tenant*</Label>
                <Select
                  value={newSiteTenantId}
                  onValueChange={(value) => {
                    setNewSiteTenantId(value)
                  }}
                  name="site-tenant"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a tenant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.length > 0 && (
                      <>
                        {[...tenants].sort((a, b) => a.name.localeCompare(b.name)).map((tenant) => (
                          <SelectItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <GoogleMapPin
                setCurrentLocation={true}
                coords={siteCoords ?? { latitude: 51, longitude: -0.6 }} // Placeholder coords; replace with actual observation coords
                onZoomChange={(zoom) => {
                  setSiteZoom(zoom);
                }}
                onChange={(coords) => {
                  setSiteCoords(coords);
                }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateMap} disabled={!newMapName.trim() || isCreating}>
                {isCreating ? "Creating..." : "Create Map"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Map</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to permanently delete "{selectedMapForDelete?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setSelectedMapForDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteMap} className="bg-red-600 hover:bg-red-700">
                Delete Map
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
